import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';

import 'translations/translations';

const useStyles = makeStyles((theme) => ({
  error: {
    color: theme.palette.error.main,
    textAlign: 'right',
    fontSize: '0.8rem',
  },
}));

type ValidatedPasswordInputProps = {
  inputName: string;
  autoFocus: boolean;
  handleChangeCallback: (name: string, value: string, error: boolean) => void;
};

const ValidatedPasswordInput: React.FC<ValidatedPasswordInputProps> = (
  props,
) => {
  const { inputName, autoFocus, handleChangeCallback } = props;
  const classes = useStyles();

  const [input, setInput] = useState<{ password?: string; repeat?: string }>(
    {},
  );

  const [errorMessage, setErrorMessage] = useState({
    password: '',
    repeat: '',
  });

  const { t } = useTranslation(['translations']);

  const handleChange = (e: React.FocusEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setInput({ ...input, [name]: value });
  };

  useEffect(() => {
    const lowerCaseRE = /^(?=.*[a-z])/;
    const upperCaseRE = /^(?=.*[A-Z])/;
    const numberRE = /^(?=.*[0-9])/;
    const specialRE = /^(?=.*\W)/; // non-alphanumeric
    const minimumRE = /^(?=.{6,})/;

    const requirements = [
      { re: lowerCaseRE, desc: t('validation.lowercase') },
      { re: upperCaseRE, desc: t('validation.uppercase') },
      { re: numberRE, desc: t('validation.number') },
      { re: specialRE, desc: t('validation.special') },
      { re: minimumRE, desc: t('validation.minimum_n', { count: 6 }) },
    ];

    const errors = { password: '', repeat: '' };

    // only validate after first input change
    if (input.password !== undefined) {
      if (!input.password) {
        errors.password = t('validation.required');
      } else {
        const validationErrors = requirements.filter(
          ({ re }) => !input.password || !re.test(input.password),
        );
        if (validationErrors.length > 0) {
          errors.password = `${validationErrors
            .map(({ desc }) => desc)
            .join('. ')}.`;
        }
      }
    }

    // only validate after first input change
    if (input.repeat !== undefined) {
      if (!input.repeat) {
        errors.repeat = t('validation.required');
      } else if (!!input.repeat && input.repeat !== input.password) {
        errors.repeat = t('validation.password_mismatch');
      }
    }

    setErrorMessage(errors);

    if (input.password && input.repeat && !errors.password && !errors.repeat) {
      handleChangeCallback(inputName, input.password, false);
    } else {
      handleChangeCallback(inputName, '', true);
    }
  }, [input]);

  return (
    <>
      <TextField
        type="password"
        variant="outlined"
        margin="normal"
        required={true}
        fullWidth
        id={inputName}
        label={t('validation.choose_password')}
        name="password"
        autoFocus={autoFocus}
        onChange={handleChange}
        onBlur={handleChange}
        value={input.password ? input.password : ''}
      />

      <Typography className={classes.error}>
        {!!errorMessage.password && errorMessage.password}
      </Typography>

      <TextField
        type="password"
        variant="outlined"
        margin="normal"
        required={true}
        fullWidth
        id="repeat"
        label={t('validation.repeat_password')}
        name="repeat"
        autoFocus={false}
        onChange={handleChange}
        onBlur={handleChange}
        value={input.repeat ? input.repeat : ''}
      />

      <Typography className={classes.error}>
        {!!errorMessage.repeat && errorMessage.repeat}
      </Typography>
    </>
  );
};

export { ValidatedPasswordInput };
